import React, { lazy, Suspense } from "react";
import { Route, Routes } from "react-router";
import { BrowserRouter as Router } from "react-router";

import { MainRoute } from "./routes/main-route";
import { StorefrontPages } from "./routes/storefront-pages";
import { FallbackRoute } from "./routes/fallback-route";

import { AuthLoginHandler } from "./domain/auth/user-auth/AuthLoginHandler";

import { AppProps } from "./index";
import { SitemapRouterRoutes } from "./routes/sitemap/router-routes";
import { GuidedSellingPreviewRouterRoutes } from "./routes/guided-selling-preview/router-routes";
import { DevLoadedStorefrontPage, DevFontsTestPage } from "./DevLoadedStorefrontPage";
import { AppShell } from "./AppShell";

const ToolbeltRouteLayout = lazy(() => import("./routes/toolbelt/shared/RouteLayout"));
const ToolbeltLoginRoute = lazy(() => import("./routes/toolbelt/LoginRoute"));
const ToolbeltTranslationsRoute = lazy(() => import("./routes/toolbelt/TranslationsRoute"));
const ToolbeltSitemapRoute = lazy(() => import("./routes/toolbelt/SitemapRoute"));
const ToolbeltRevisionsRoute = lazy(() => import("./routes/toolbelt/RevisionsRoute"));
const ToolbeltProductCatalogRoute = lazy(() => import("./routes/toolbelt/ProductCatalogRoute"));

function App(props: AppProps) {
  const additionalRoutes =
    process.env.NODE_ENV === "development"
      ? [
          <Route key="fonts" path="/fonts" /*exact*/ Component={DevFontsTestPage} />,
          <Route key="dev" path="/__dev" /*exact*/ element={<DevLoadedStorefrontPage config={props.config} />} />,
        ]
      : [];

  return (
    <AppShell config={props.config}>
      <Router>
        <Routes>
          {additionalRoutes}

          <Route path="/auth/login" /*exact*/ Component={AuthLoginHandler} />

          <Route
            path="/toolbelt-login"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ToolbeltLoginRoute />
              </Suspense>
            }
          />

          <Route
            path="/toolbelt"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <ToolbeltRouteLayout />
              </Suspense>
            }
          >
            <Route
              path="translations"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <ToolbeltTranslationsRoute />
                </Suspense>
              }
            />
            <Route
              path="sitemap"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <ToolbeltSitemapRoute />
                </Suspense>
              }
            />
            <Route
              path="revisions"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <ToolbeltRevisionsRoute />
                </Suspense>
              }
            />
            <Route
              path="product-catalog"
              element={
                <Suspense fallback={<div>Loading...</div>}>
                  <ToolbeltProductCatalogRoute />
                </Suspense>
              }
            />
          </Route>

          {props.runtimeMode === "spa" && [
            <Route key={"main-route"} path="/" /*exact*/ Component={MainRoute} />,
            ...SitemapRouterRoutes,
            ...GuidedSellingPreviewRouterRoutes,
            <Route
              key={"fallback-route"}
              path={`/:countryIsoCode?/:languageIsoCode?`}
              /*exact*/
              Component={FallbackRoute}
            />,
          ]}

          <Route path="*" element={<StorefrontPages config={props.config} />} />
        </Routes>
      </Router>
    </AppShell>
  );
}

export default App;
